import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiFormRequest, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './HostEvent.css';

/* Breakouts — every draw of the day, and the work the one-press card on the host screen cannot
   hold: correcting who is in the room, naming groups and their rooms, moving a person, and
   filling it in from a spreadsheet instead of drawing it.

   The split is the same one the run sheet makes: the host screen carries the gesture you make
   while a room waits, this page carries the writing.

   ★ Drawing and telling are ONE press by default. The owner asked for "press a button and they
   get a notification", so `notify` rides on the draw; "Distribute only" is the quieter second
   button for a host who wants to look first. An unpublished draw is on nobody's home and rings
   nobody, which is what makes looking first safe.

   ★ The pool is the ATTENDED, pre-ticked, with everybody registered underneath. The scanner is
   not used at every event, so a host who can see the room has to be able to say so — but the
   server re-derives eligibility on every write, because a screen that offered somebody is not a
   guard that they belong.

   ★ Uploading is two steps. `/import` reads the file and hands back what it MEANS; the host sees
   the matched count, the addresses we do not know and the people who are not on this event, and
   only then presses distribute. A file that seats people the moment it is picked is how a
   mis-clicked download ends up on a wall. */

// What the number field will accept, mirroring mutations/breakouts. Stated here so the field
// cannot ask for something the server is about to refuse.
const MIN_GROUPS = 2;
const MAX_GROUPS = 50;

const HostBreakouts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [count, setCount] = useState(4);
  const [name, setName] = useState('');
  const [slotId, setSlotId] = useState('');
  const [picked, setPicked] = useState(null);        // Set of partner ids, null = "not touched"
  const [showPool, setShowPool] = useState(false);
  const [parsed, setParsed] = useState(null);        // what the uploaded file means
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/breakouts`)
      .then(setData)
      // Not the admin, not a host that may run it, or not this event — the host screen says so.
      .catch(() => navigate(`/host-event/${eventId}`));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const pool = useMemo(() => (data && data.pool) || [], [data]);
  const breakouts = (data && data.breakouts) || [];
  const slots = (data && data.slots) || [];

  /* Who gets drawn. Untouched means "the people who scanned in", which is the answer for almost
     every event; the moment a host ticks anything the selection is theirs and is used verbatim.
     Falls back to EVERYONE when nobody has scanned — an event that did not use the scanner would
     otherwise offer a button that can only fail. */
  const selected = useMemo(() => {
    if (picked) return [...picked];
    const attended = pool.filter((p) => p.attended).map((p) => p.id);
    return attended.length ? attended : pool.map((p) => p.id);
  }, [picked, pool]);

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev || selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const adopt = (res, text) => {
    setData((d) => ({ ...d, breakouts: res.breakouts }));
    setNotice(text);
  };

  const fail = (err) => setNotice(apiErrorText(t, err));

  /* --- drawing -------------------------------------------------------------------------- */

  const distribute = (notify) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const body = parsed
      ? { assignment: parsed.assignment, name, planning_id: slotId, notify }
      : { partner_ids: selected, group_count: count, name, planning_id: slotId, notify };
    apiRequest('POST', `/company/event/${eventId}/breakouts`, body)
      .then((res) => {
        adopt(res, notify ? t('breakoutTold', { n: res.notified }) : t('breakoutDrawn'));
        setParsed(null);
        setName('');
        if (fileRef.current) fileRef.current.value = '';
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const tell = (id) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/breakout/${id}/notify`)
      .then((res) => adopt(res, t('breakoutTold', { n: res.notified })))
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const drop = (id) => {
    if (busy) return;
    setBusy(true);
    apiRequest('DELETE', `/company/event/${eventId}/breakout/${id}`)
      .then((res) => adopt(res, t('breakoutDeleted')))
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const renameGroup = (breakoutId, groupId, fields) => {
    apiRequest('PUT', `/company/event/${eventId}/breakout/${breakoutId}/group/${groupId}`, fields)
      .then((res) => setData((d) => ({ ...d, breakouts: res.breakouts })))
      .catch(fail);
  };

  const moveTo = (breakoutId, partnerId, groupId) => {
    apiRequest('POST', `/company/event/${eventId}/breakout/${breakoutId}/member`,
               { partner_id: partnerId, group_id: groupId })
      .then((res) => adopt(res, t('breakoutMoved')))
      .catch(fail);
  };

  /* --- the spreadsheet ------------------------------------------------------------------- */

  const upload = (file) => {
    if (!file || busy) return;
    setBusy(true);
    setNotice(null);
    const form = new FormData();
    form.append('file', file);
    apiFormRequest(`/company/event/${eventId}/breakouts/import`, form)
      .then((res) => {
        setParsed(res);
        setNotice(t('breakoutFileRead', { n: res.assignment.length, groups: res.groups.length }));
      })
      .catch((err) => { setParsed(null); fail(err); })
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  const attendedCount = data.attended_count || 0;

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/host-event/${eventId}`} />
        <Title my="1" className="mt-3">
          <Icon name="users" theme="primary" className="me-2" />
          {t('breakouts')}
        </Title>
        <p className="ce-note">{t('breakoutsHint')}</p>

        {/* --- draw ------------------------------------------------------------------- */}
        <div className="bravos-form">
          <p className="bravos-cal__label mb-1">
            {attendedCount
              ? t('breakoutPoolAttended', { n: attendedCount, total: pool.length })
              : t('breakoutPoolNobodyScanned', { total: pool.length })}
          </p>
          <button type="button" className="hv-bo__act" onClick={() => setShowPool(!showPool)}>
            {showPool ? t('hideWhoIsIn') : t('changeWhoIsIn', { n: selected.length })}
          </button>

          {showPool && (
            <div className="hv-bo__pool mt-2">
              {pool.map((p) => (
                <label key={p.id} className="hv-bo__poolrow">
                  <input
                    type="checkbox"
                    checked={selected.includes(p.id)}
                    onChange={() => toggle(p.id)}
                  />
                  <span className="hv-bo__name">{p.name || p.email}</span>
                  {p.attended && <Icon name="user-check" />}
                </label>
              ))}
              {!pool.length && <p className="ce-note mb-0">{t('breakoutNobodyHere')}</p>}
            </div>
          )}

          <label className="bravos-form__label mt-2">
            {t('breakoutName')}
            <input
              type="text"
              value={name}
              maxLength={100}
              placeholder={t('breakoutNamePlaceholder')}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="bravos-form__label">
            {t('breakoutOnRunSheet')}
            <select value={slotId} onChange={(e) => setSlotId(e.target.value)}>
              <option value="">{t('breakoutNoSlot')}</option>
              {slots.map((s) => (
                <option key={s.id} value={s.id}>
                  {`${(s.start || '').slice(11, 16)} ${s.title}`.trim()}
                </option>
              ))}
            </select>
          </label>

          {/* The uploaded file replaces the draw entirely — the count field would be a lie next
              to it, so it is hidden rather than disabled. */}
          {parsed ? (
            <div className="mt-2">
              <p className="mb-1">
                {t('breakoutFileReady', { n: parsed.assignment.length,
                                          groups: parsed.groups.length })}
              </p>
              {parsed.unmatched.length > 0 && (
                <p className="ce-note mb-1">
                  {t('breakoutUnmatched', { n: parsed.unmatched.length,
                                            list: parsed.unmatched.slice(0, 5).join(', ') })}
                </p>
              )}
              {parsed.off_event.length > 0 && (
                <p className="ce-note mb-1">
                  {t('breakoutOffEvent', { n: parsed.off_event.length,
                                           list: parsed.off_event.slice(0, 5).join(', ') })}
                </p>
              )}
              <button type="button" className="hv-bo__act" onClick={() => setParsed(null)}>
                {t('breakoutDiscardFile')}
              </button>
            </div>
          ) : (
            <div className="hv-bo__draw mt-2">
              <label className="bravos-form__label hv-bo__n">
                {t('breakoutGroups')}
                <input
                  type="number"
                  min={MIN_GROUPS}
                  max={MAX_GROUPS}
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value) || MIN_GROUPS)}
                />
              </label>
            </div>
          )}

          <div className="hv-bo__draw mt-2">
            <button
              type="button"
              className="bravos-submit"
              disabled={busy || (!parsed && !selected.length)}
              onClick={() => distribute(true)}
            >
              {t('breakoutDistributeAndTell')}
            </button>
            <button
              type="button"
              className="hv-bo__act"
              disabled={busy || (!parsed && !selected.length)}
              onClick={() => distribute(false)}
            >
              {t('breakoutDistributeOnly')}
            </button>
          </div>

          <p className="bravos-cal__label mt-3 mb-1">{t('breakoutFromFile')}</p>
          <p className="ce-note">{t('breakoutFromFileHint')}</p>
          <div className="hv-bo__draw">
            <a
              className="hv-bo__act"
              href={`/api/company/event/${eventId}/breakouts/template.xlsx`}
            >
              <Icon name="file-download" className="me-1" />{t('breakoutTemplate')}
            </a>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.csv,text/csv"
              onChange={(e) => upload(e.target.files && e.target.files[0])}
            />
          </div>
        </div>

        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      {/* --- what has been drawn ------------------------------------------------------- */}
      {breakouts.map((b) => (
        <Card key={b.id}>
          <div className="hv-bo__round">
            <strong className="hv-bo__name">{b.name || t('breakoutUnnamed')}</strong>
            <span className="hv-bo__when">
              {[t('breakoutNGroups', { n: b.groups.length, people: b.member_count }),
                b.planning_title,
                b.is_published ? t('breakoutToldAt',
                                   { time: (b.published_at || '').slice(11, 16) })
                               : t('breakoutNotToldYet')]
                .filter(Boolean).join(' · ')}
            </span>
          </div>

          <div className="hv-bo__draw mb-2">
            <button type="button" className="hv-bo__act" disabled={busy}
                    onClick={() => tell(b.id)}>
              <Icon name="megaphone" className="me-1" />
              {b.is_published ? t('breakoutTellAgain') : t('breakoutTellThem')}
            </button>
            <button type="button" className="hv-bo__act"
                    onClick={() => navigate(`/host-event/${eventId}/breakout-board?b=${b.id}`)}>
              <Icon name="megaphone" className="me-1" />{t('project')}
            </button>
            <button type="button" className="hv-bo__act" disabled={busy}
                    onClick={() => drop(b.id)}>
              <Icon name="cross-circle" className="me-1" />{t('delete')}
            </button>
          </div>

          {b.groups.map((g) => (
            <div key={g.id} className="hv-bo__group">
              <div className="hv-bo__grouphead">
                <input
                  type="text"
                  defaultValue={g.name}
                  maxLength={100}
                  placeholder={t('breakoutGroupN', { n: g.sort_order })}
                  onBlur={(e) => (e.target.value !== g.name)
                    && renameGroup(b.id, g.id, { name: e.target.value })}
                />
                <input
                  type="text"
                  defaultValue={g.location}
                  maxLength={100}
                  placeholder={t('breakoutWhere')}
                  onBlur={(e) => (e.target.value !== g.location)
                    && renameGroup(b.id, g.id, { location: e.target.value })}
                />
              </div>
              {g.members.map((m) => (
                <div key={m.id} className="hv-bo__member">
                  {/* Moving somebody is a select, not a drag: this is used one-handed on a phone
                      in a room, and a drop target is a thing you miss. */}
                  <select
                    value={g.id}
                    onChange={(e) => moveTo(b.id, m.id, e.target.value)}
                  >
                    {b.groups.map((other) => (
                      <option key={other.id} value={other.id}>
                        {(other.name || '').trim()
                          || t('breakoutGroupN', { n: other.sort_order })}
                      </option>
                    ))}
                  </select>
                  <span className="hv-bo__name">{m.name}</span>
                  {b.is_published && !m.notified && (
                    <span className="hv-bo__pending">{t('breakoutNotToldYet')}</span>
                  )}
                </div>
              ))}
              {!g.members.length && <p className="ce-note mb-0">{t('breakoutEmptyGroup')}</p>}
            </div>
          ))}
        </Card>
      ))}

      {!breakouts.length && (
        <Card><p className="ce-note mb-0">{t('noBreakoutYet')}</p></Card>
      )}
    </Base>
  );
};

export default HostBreakouts;
